import Cookie from '/app/utils/cookie';
import { sentry } from '/app/plugins/sentry';

const DEFAULT_WARN_MS = 3000;
const DEFAULT_CRITICAL_MS = 8000;

/**
 * Durations beyond this are not real latency: the machine slept or the tab
 * was suspended mid-request (timers freeze, so even the axios timeout never
 * fired). Reporting them as "slow requests" only creates noise.
 */
const SUSPEND_ARTIFACT_MS = 5 * 60 * 1000;

/**
 * Endpoints we never want to notify about as "slow".
 *
 * These are either:
 *   - Known heavy aggregations that take seconds by design.
 *   - External URLs (e.g. S3 signed upload PUTs) whose latency depends on
 *     the user's connection, not on our infra.
 *   - Long-poll endpoints whose duration is *expected* to be high.
 *
 * Add new patterns sparingly — the goal is to surface real regressions.
 */
export const DEFAULT_IGNORED_ENDPOINTS = [
    // Brain neurons aggregation (also on Sentry `denyUrls`).
    /\/brain\/[^/]+\/neurons/i,
    // Heavy analytics / stats aggregations.
    /^\/analytics(\/|$)/i,
    /^\/stats(\/|$)/i,
    // External signed upload URLs (S3, GCS).
    /^https?:\/\/[^/]*amazonaws\.com\//i,
    /^https?:\/\/[^/]*googleapis\.com\//i,
    /^https?:\/\/[^/]*googleusercontent\.com\//i,
    // Long-poll state checks (see app/services/polling).
    /\/state(?:\?|$)/i,
];

const stringify = (value) => {
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return value || 'no';
};

const matches = (url, pattern) => {
    if (!url || pattern == null) return false;
    if (pattern instanceof RegExp) return pattern.test(url);
    if (typeof pattern === 'function') return Boolean(pattern(url));
    if (typeof pattern === 'string') return url.includes(pattern);
    return false;
};

const isIgnored = (url, patterns) => patterns.some((p) => matches(url, p));

/**
 * Normalize URL for fingerprinting so that the same logical endpoint
 * groups under a single Sentry issue (no issue-per-id explosion).
 */
const fingerprintUrl = (url = '') => {
    return url
        .split('?')[0]
        .replace(
            /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
            ':uuid',
        )
        .replace(/\/\d+(?=\/|$)/g, '/:id');
};

const resolveThresholds = (config, options) => {
    const override = typeof config?.slowRequest === 'object' ? config.slowRequest : null;
    return {
        warn: override?.warn ?? options.warnThreshold ?? DEFAULT_WARN_MS,
        critical: override?.critical ?? options.criticalThreshold ?? DEFAULT_CRITICAL_MS,
    };
};

/**
 * Track slow but successful requests and notify Sentry.
 *
 * Failed requests already go through `captureException`; this middleware
 * focuses on requests that **succeed past a latency budget**, which would
 * otherwise be invisible in error monitoring.
 *
 * @param {import('axios').AxiosInstance} axios
 * @param {object} [options]
 * @param {number} [options.warnThreshold=3000]      ms → captures `warning`
 * @param {number} [options.criticalThreshold=8000]  ms → captures `error`
 * @param {Array<string|RegExp|((url:string) => boolean)>} [options.ignore]
 *   Extra patterns to skip. Merged with `DEFAULT_IGNORED_ENDPOINTS`.
 *
 * Per-request override (set on the axios config):
 *   - `config.slowRequest = false`           → disable tracking
 *   - `config.slowRequest = { warn, critical }` → custom thresholds
 */
export const slowRequest = (axios, options = {}) => {
    const ignore = [...DEFAULT_IGNORED_ENDPOINTS, ...(options.ignore ?? [])];

    // `captureException` also sets `startTime`; we set it defensively in case
    // this middleware is registered standalone.
    axios.interceptors.request.use((config) => {
        if (!config.startTime) {
            config.startTime = Date.now();
        }
        return config;
    });

    axios.interceptors.response.use((response) => {
        const { config = {}, status } = response;

        if (config.slowRequest === false) return response;
        if (isIgnored(config.url, ignore)) return response;

        // Skip intermediate retry responses — duration is misleading and we
        // don't want to double-report. The final attempt will be tracked.
        if (config.retryState && !config.retryState.isLastAttempt) {
            return response;
        }

        const start = config.startTime ?? Date.now();
        const duration = Date.now() - start;
        const { warn, critical } = resolveThresholds(config, options);

        if (duration < warn) return response;
        if (duration >= SUSPEND_ARTIFACT_MS) return response;

        const level = duration >= critical ? 'error' : 'warning';
        const bucket = duration >= critical ? 'critical' : 'warn';
        const method = (config.method || 'get').toUpperCase();
        const path = fingerprintUrl(config.url);

        sentry.withScope((scope) => {
            scope.setLevel(level);
            scope.setTag('event', 'slow-request');
            scope.setTag('slow.bucket', bucket);
            scope.setTag('http.method', method);
            scope.setTag('http.status', status);
            scope.setFingerprint(['slow-request', method, path]);

            scope.setContext('request', {
                url: `${config.baseURL ?? ''}${config.url}`,
                method,
                params: stringify(config.params),
                duration_ms: duration,
                threshold_ms: warn,
                critical_ms: critical,
                retries: config.retryState?.count ?? 0,
                timeout_ms: config.timeout,
                fingerprint: Cookie.get('fingerprint'),
            });

            sentry.captureMessage(
                `Slow request (${duration}ms) ${method} ${path}`,
                level,
            );
        });

        return response;
    });
};
